import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import {
  setupRequestAsync,
  fetchApi,
  fetchApiOrThrow
} from "~/utils";
import { squireApiConfig } from "~/config";

const { url } = squireApiConfig;

export class ContactEditorApiService {
  // fetchApiOrThrow, not fetchApi: a swallowed 400 returned undefined, which the editor handed to
  // onContactChanged as though the save had succeeded — closing the popup and losing the edits.
  async createContact(values) {
    const fetchUrl = `${url}/v1/contact-editor/create-contact`;
    const formBody = new URLSearchParams({
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }

  async updateContact(contactId, values) {
    const fetchUrl = `${url}/v1/contact-editor/update-contact`;
    const formBody = new URLSearchParams({
      contactId: contactId,
      values: JSON.stringify(values),
    });
    return await fetchApiOrThrow(fetchUrl, "PUT", formBody);
  }

  async getContactDetails(contactId) {
    const fetchUrl = `${url}/v1/contact-editor/contact-details/${contactId}`;
    const result = await fetchApi(fetchUrl, "GET");
    if (result) {
      result.companyContacts = (result.companyContacts?.$values ?? result.companyContacts ?? []).map(cc => ({
        ...cc,
        phoneNumbers: cc.phoneNumbers?.$values ?? cc.phoneNumbers ?? [],
        emailAddresses: cc.emailAddresses?.$values ?? cc.emailAddresses ?? [],
      }));
    }
    return result;
  }

  getClientRelationshipTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/client-relationship-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getContactPrioritiesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/contact-priorities`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getRepresentativesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/representatives`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getPhoneTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/phone-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  getProjectContactTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/project-contact-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "value",
    });
  }

  // Reads the contact editor's own companies route rather than /v1/companies/, which is the
  // companies-page grid query and is gated on CanViewCompaniesPage — a contacts-only user got an
  // empty dropdown from it. Sorted so the picker is alphabetical (WK-565).
  getCompaniesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/contact-editor/companies`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "displayName",
    });
  }

  getCompanyTypesDataSource() {
    return new DataSource({
      store: createStore({
        key: "id",
        loadUrl: `${url}/v1/company-editor/company-types`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "name",
    });
  }

  async createCompany(values) {
    const fetchUrl = `${url}/v1/company-editor/create-company`;
    const formBody = new URLSearchParams({ values: JSON.stringify(values) });
    return await fetchApiOrThrow(fetchUrl, "POST", formBody);
  }

  getAllContactsDataSource() {
    return new DataSource({
      store: createStore({
        key: "contactId",
        loadUrl: `${url}/v1/contact-selector/contacts`,
        onBeforeSend: async (method, ajaxOptions) =>
          await setupRequestAsync(method, ajaxOptions),
      }),
      sort: "fullName",
    });
  }
}
